/**
 * useLocaleViewModel — MVVM ViewModel for the locale switcher (issue #140, #221).
 *
 * Responsibilities:
 *   - resolve the currently active locale FROM THE URL (`/:locale/...`), which is
 *     the source of truth since issue #221; localStorage remains the
 *     cross-session memory of the user's last explicit pick
 *   - load the per-store locales catalog from ConfigService at
 *     `/config/<store>/locales` and expose it to the View
 *   - keep i18next in sync with the active URL locale
 *   - on an explicit pick (`setLocale`): persist to localStorage AND rewrite the
 *     URL locale segment in place via history replace (no back-button pollution)
 *
 * The `?i18n=keys` / `?i18n=missing` debug mode is owned entirely by the
 * keyDisplay i18next post-processor (it reads the URL on each t() call), so
 * the viewmodel deliberately does NOT expose a debug flag.
 *
 * Pattern mirrors the existing useCartViewModel / useCategoriesViewModel:
 * pure hook, React state, no JSX. The View is `LocaleSwitcher.tsx`.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchRuntimeConfig, getCachedConfig } from "../services/config";
import { splitLocaleFromPath, swapLocaleInPath } from "../i18n/localeRouting";

const LOCALE_STORAGE_KEY = "intersum.locale";
const FALLBACK_LOCALE = "en-US";

export type SwitcherStyle = "pill" | "minimal" | "iconOnly";

export interface LocaleCatalogEntry {
	locale: string;
	currency: string;
}

export interface LocalesCatalog {
	default: string;
	supported: LocaleCatalogEntry[];
	switcherStyle: SwitcherStyle;
}

export interface UseLocaleViewModel {
	currentLocale: string;
	availableLocales: LocaleCatalogEntry[];
	switcherStyle: SwitcherStyle;
	isReady: boolean;
	setLocale: (locale: string) => void;
}

function readStoredLocale(): string | null {
	if (typeof window === "undefined") return null;
	try {
		const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
		if (stored && /^[a-z]{2}-[A-Z]{2}$/.test(stored)) return stored;
	} catch {
		// localStorage may throw in private-mode browsers; ignore.
	}
	return null;
}

function writeStoredLocale(locale: string): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	} catch {
		// localStorage may throw; not fatal — locale will reset on reload.
	}
}

async function fetchLocalesCatalog(apiGatewayUrl: string, storeLabel: string): Promise<LocalesCatalog> {
	const base = apiGatewayUrl.replace(/\/$/, "");
	const url = `${base}/config/${storeLabel}/locales`;
	const response = await fetch(url, { credentials: "omit" });
	if (!response.ok) {
		throw new Error(`Failed to load locales catalog (HTTP ${response.status})`);
	}
	return response.json();
}

export function useLocaleViewModel(): UseLocaleViewModel {
	const { i18n } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	// Seed synchronously from the already-loaded runtime config when it carries
	// the locales block (issue #140). The App gates rendering on config load, so
	// getCachedConfig() is populated by the time the switcher mounts — this lets
	// the switcher render on first paint with no async-catalog gate (no CLS, no
	// boot→default flash). The effect below is the fallback for configs that
	// don't embed locales yet.
	const [catalog, setCatalog] = useState<LocalesCatalog | null>(
		() => getCachedConfig()?.locales ?? null,
	);

	const availableLocales = catalog?.supported ?? [];

	// The URL is the source of truth for the active locale (issue #221). The
	// stored pick / i18next / global fallback only fill the gap before the URL has
	// resolved — LocaleGate redirects bare paths to a `/:locale` prefix, so in
	// steady state `urlLocale` is always set.
	const urlLocale = splitLocaleFromPath(location.pathname, availableLocales).locale;
	const currentLocale = urlLocale ?? readStoredLocale() ?? i18n.language ?? FALLBACK_LOCALE;

	// Load the catalog — fallback for runtime configs that don't embed `locales`.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// Prefer the catalog embedded in the already-loaded runtime config.
				let resolved: LocalesCatalog | null = getCachedConfig()?.locales ?? null;
				if (!resolved) {
					// Fallback: runtime config didn't carry locales — fetch the
					// dedicated catalog endpoint.
					const runtime = await fetchRuntimeConfig();
					const apiGatewayUrl: string =
						(runtime.medusa?.backendUrl ?? "")
							.replace(/\/api\/v1\/tenants\/[^/]+.*$/, "")
							.replace(/\/store\/?$/, "") || "";
					const storeLabel: string = runtime.tenantPrefix ?? runtime.storeLabel ?? "intersum-ecommerce-example";
					resolved = await fetchLocalesCatalog(apiGatewayUrl, storeLabel);
				}
				if (cancelled || !resolved) return;
				setCatalog(resolved);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn("[useLocaleViewModel] failed to load locales catalog:", error);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// Keep i18next aligned with the active URL locale. Deep links, back/forward
	// navigation, and the post-redirect landing all flow through here.
	useEffect(() => {
		if (urlLocale && i18n.language !== urlLocale) {
			i18n.changeLanguage(urlLocale).catch((error) => {
				// eslint-disable-next-line no-console
				console.warn(`[useLocaleViewModel] changeLanguage(${urlLocale}) failed:`, error);
			});
		}
	}, [urlLocale, i18n]);

	const setLocale = useCallback(
		(locale: string) => {
			// Persist the explicit pick as cross-session memory ...
			writeStoredLocale(locale);
			// ... flip i18next immediately for a snappy text update ...
			i18n.changeLanguage(locale).catch((error) => {
				// eslint-disable-next-line no-console
				console.warn(`[useLocaleViewModel] changeLanguage(${locale}) failed:`, error);
			});
			// ... and rewrite the URL locale segment in place. replace = no
			// back-button pollution; the View re-renders with currentLocale derived
			// from the updated path.
			const target = swapLocaleInPath(
				locale,
				location.pathname,
				availableLocales,
				location.search,
				location.hash,
			);
			navigate(target, { replace: true });
		},
		[i18n, navigate, location.pathname, location.search, location.hash, availableLocales],
	);

	const switcherStyle: SwitcherStyle = catalog?.switcherStyle ?? "pill";
	const isReady = catalog !== null;

	return {
		currentLocale,
		availableLocales,
		switcherStyle,
		isReady,
		setLocale,
	};
}

export default useLocaleViewModel;
